import { LitElement, html, css, unsafeCSS } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { property, state } from 'lit/decorators.js';
import { animate, stagger } from 'motion';

export interface ToolbarItem {
  type: 'button' | 'dropdown' | 'divider' | 'color-picker' | 'input';
  id?: string;
  icon?: string;
  label?: string;
  tooltip?: string;
  command?: string;
  value?: any;
  disabled?: boolean;
  active?: boolean;
  dropdown?: DropdownConfig;
  input?: InputConfig;
  onClick?: (item: ToolbarItem, event: Event) => void;
  group?: string;
}

export interface DropdownConfig {
  items: DropdownItem[];
  editable?: boolean;
  searchable?: boolean;
  placeholder?: string;
  width?: string;
  onSelect?: (item: DropdownItem, parent: ToolbarItem) => void;
}

export interface DropdownItem {
  label: string;
  value: any;
  icon?: string;
  preview?: string;
  command?: string;
  disabled?: boolean;
  divider?: boolean;
}

export interface InputConfig {
  type: 'text' | 'number';
  placeholder?: string;
  width?: string;
  min?: number;
  max?: number;
  step?: number;
  value?: any;
  onInput?: (value: any, parent: ToolbarItem) => void;
  onChange?: (value: any, parent: ToolbarItem) => void;
  onEnter?: (value: any, parent: ToolbarItem) => void;
}

export class RdFormatToolbar extends LitElement {
  @property({ type: Array }) items: ToolbarItem[] = [];
  @property({ type: Boolean, reflect: true }) compact: boolean = false;
  @property({ type: Boolean, reflect: true }) sticky: boolean = false;
  @property({ type: String, reflect: true }) position: 'top' | 'bottom' = 'top';
  @property({ type: Boolean, reflect: true }) showArrow: boolean = false;
  @property({ type: String, reflect: true }) arrowPosition: 'top' | 'bottom' = 'top';
  @property({ type: Number, reflect: true }) arrowSize: number = 6;
  @property({ type: Boolean, reflect: true }) visible: boolean = true;
  @state() private _activeFormats: Set<string> = new Set();
  @state() private _selectedDropdownValues: Map<string, DropdownItem> = new Map();

  // Light DOM
  createRenderRoot() {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    this.style.setProperty('--arrow-size', `${this.arrowSize}px`);
    this['_lastVisible'] = this.visible;
  }

  firstUpdated() {
    this._updateArrowClass();
  }

  updated(changedProperties: Map<string, any>) {
    super.updated(changedProperties);
    if (changedProperties.has('showArrow') || changedProperties.has('arrowPosition')) {
      this._updateArrowClass();
    }
    if (changedProperties.has('arrowSize')) {
      this.style.setProperty('--arrow-size', `${this.arrowSize}px`);
    }
    if (changedProperties.has('visible')) {
      if (this.visible !== this['_lastVisible']) {
        this._showHide();
      }
    }
  }
  _showHide() {
    this['_lastVisible'] = this.visible;
    if (this.visible) {
      this.style.pointerEvents = 'auto';
      animate(this, { opacity: [0, 1] }, { duration: 0.5 }).finished.then(() => {
        this.style.transition = 'top 0.3s ease, bottom 0.3s ease';
      });
    } else {
      animate(this, { opacity: [1, 0] }, { duration: 0.5 }).finished.then(() => {
        this.style.transition = '';
        this.style.pointerEvents = 'none';
      });
    }
  }
  private _updateArrowClass() {
    // Remove existing arrow classes
    this.classList.remove('top-arrow', 'bottom-arrow');

    // Add new arrow class if showArrow is true
    if (this.showArrow) {
      this.classList.add(`${this.arrowPosition}-arrow`);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  private _handleItemClick(item: ToolbarItem, event: Event) {
    if (item.disabled) return;

    if (item.type === 'button') {
      if (item.onClick) {
        item.onClick(item, event);
        this.requestUpdate();
      }
      if (item.command) {
        this._executeCommand(item.command, item.value);
      }
      // this._toggleActive(item);
    } else if (item.type === 'dropdown') {
      this._showDropdown(item, event);
    } else if (item.type === 'color-picker') {
      this._showColorPicker(item, event);
    }
  }

  private _showDropdown(item: ToolbarItem, event: Event) {
    event.stopPropagation();
    if (!item.dropdown) return;
    if ((window as any).ColorPicker?.getInstance()?.open) {
      (window as any).ColorPicker?.close();
    }
    if ((window as any).DropdownPanel?.getInstance()?.open) {
      (window as any).DropdownPanel?.close();
      let t = (window as any).DropdownPanel?.getInstance()?.target;
      if (t === event.currentTarget) return;
    }

    const button = event.currentTarget as HTMLElement;
    const selectedValue = item.id ? this._selectedDropdownValues.get(item.id) : null;

    // Convert dropdown items to DropdownPanel format
    const panelItems = item.dropdown.items.map((dropdownItem) => ({
      leftIcon: dropdownItem.icon || '',
      value: dropdownItem.value,
      label: dropdownItem.label,
      rightIcon: '',
      groupName: '',
      selected: selectedValue ? selectedValue.value === dropdownItem.value : false,
      disabled: dropdownItem.disabled || false,
    }));

    (window as any).DropdownPanel.show({
      target: button,
      placement: 'bottom',
      offsetY: 4,
      offsetX: 0,
      searchable: item.dropdown.searchable || true,
      items: panelItems,
      closeOnSelect: true,
      multiSelection: false,
      size: 'fit',
      id: 'toolbar',
      onSelect: (selectedItem: any) => {
        // Find original dropdown item
        const originalItem = item.dropdown!.items.find((di) => di.value === selectedItem.value);
        if (originalItem) {
          // Store selected value
          if (item.id) {
            this._selectedDropdownValues.set(item.id, originalItem);
          }

          if (item.dropdown!.onSelect) {
            item.dropdown!.onSelect(originalItem, item);
          }

          if (originalItem.command) {
            this._executeCommand(originalItem.command, originalItem.value);
          }

          this.requestUpdate();
        }
      },
    });
  }

  private _showColorPicker(item: ToolbarItem, event: Event) {
    event.stopPropagation();
    if ((window as any).DropdownPanel?.getInstance()?.open) {
      (window as any).DropdownPanel?.close();
    }
    if ((window as any).ColorPicker?.getInstance()?.open) {
      (window as any).ColorPicker?.close();
      let t = (window as any).ColorPicker?.getInstance()?.target;
      if (t === event.currentTarget) return;
    }
    const button = event.currentTarget as HTMLElement;

    (window as any).ColorPicker.show({
      target: button,
      placement: 'bottom',
      offsetY: 4,
      offsetX: 0,
      value: item.value || '#000000',
      id: 'toolbar',
      onColorChange: (color: string) => {
        item.value = color;
        if (item.onClick) {
          item.onClick(item, event);
        }
        this.requestUpdate();
      },
    });
  }

  private _executeCommand(command: string, value?: any) {
    this.dispatchEvent(
      new CustomEvent('toolbar-command', {
        detail: { command, value },
        bubbles: true,
        composed: true,
      })
    );
  }

  private _toggleActive(item: ToolbarItem) {
    if (item.id) {
      if (this._activeFormats.has(item.id)) {
        this._activeFormats.delete(item.id);
      } else {
        this._activeFormats.add(item.id);
      }
      this.requestUpdate();
    }
  }

  private _isActive(item: ToolbarItem): boolean {
    return item.active || (item.id ? this._activeFormats.has(item.id) : false);
  }

  private _renderButton(item: ToolbarItem) {
    const isActive = this._isActive(item);
    return html`
      <button class="toolbar-btn ${isActive ? 'active' : ''} ${item.disabled ? 'disabled' : ''}" @click="${(e: Event) => this._handleItemClick(item, e)}" title="${item.tooltip || item.label || ''}" ?disabled="${item.disabled}">
        ${item.icon ? html`<span class="toolbar-icon">${unsafeHTML(item.icon)}</span>` : ''} ${item.label ? html`<span class="toolbar-label">${item.label}</span>` : ''}
      </button>
    `;
  }

  private _renderDropdown(item: ToolbarItem) {
    const isActive = this._isActive(item);
    const selectedValue = item.id ? this._selectedDropdownValues.get(item.id) : null;

    return html`
      <button class="toolbar-btn dropdown-trigger ${isActive ? 'active' : ''} ${item.disabled ? 'disabled' : ''}" @click="${(e: Event) => this._handleItemClick(item, e)}" title="${item.tooltip || item.label || ''}" ?disabled="${item.disabled}">
        ${selectedValue?.icon ? html`<span class="toolbar-icon">${unsafeHTML(selectedValue.icon)}</span>` : item.icon ? html`<span class="toolbar-icon">${unsafeHTML(item.icon)}</span>` : ''}
        ${selectedValue?.preview ? html`<span class="toolbar-label">${selectedValue.preview}</span>` : selectedValue?.label ? html`<span class="toolbar-label">${selectedValue.label}</span>` : item.label ? html`<span class="toolbar-label">${item.label}</span>` : ''}
        <span class="dropdown-arrow">
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
            <path d="M2.5 3.5L5 6L7.5 3.5" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </span>
      </button>
    `;
  }

  private _renderDivider() {
    return html`<div class="toolbar-divider"></div>`;
  }

  private _handleInputChange(e: Event, item: ToolbarItem) {
    const input = e.target as HTMLInputElement;
    const value = item.input?.type === 'number' ? parseFloat(input.value) || 0 : input.value;

    // Update item value
    if (item.input) {
      item.input.value = value;
    }

    if (item.input?.onInput) {
      item.input.onInput(value, item);
    }
  }

  private _handleInputBlur(e: Event, item: ToolbarItem) {
    const input = e.target as HTMLInputElement;
    const value = item.input?.type === 'number' ? parseFloat(input.value) || 0 : input.value;

    if (item.input?.onChange) {
      item.input.onChange(value, item);
    }
  }

  private _handleInputKeydown(e: KeyboardEvent, item: ToolbarItem) {
    if (e.key === 'Enter') {
      const input = e.target as HTMLInputElement;
      const value = item.input?.type === 'number' ? parseFloat(input.value) || 0 : input.value;

      if (item.input?.onEnter) {
        item.input.onEnter(value, item);
      } else if (item.input?.onChange) {
        item.input.onChange(value, item);
      }

      input.blur();
    }
  }

  private _renderInput(item: ToolbarItem) {
    if (!item.input) return '';

    const inputType = item.input.type || 'text';
    const isNumber = inputType === 'number';

    return html`
      <div class="toolbar-input-wrapper">
        <input
          type="${inputType}"
          class="toolbar-input ${isNumber ? 'number-input' : 'text-input'}"
          placeholder="${item.input.placeholder || ''}"
          .value="${item.input.value ?? ''}"
          min="${item.input.min ?? ''}"
          max="${item.input.max ?? ''}"
          step="${item.input.step ?? (isNumber ? '1' : '')}"
          @input="${(e: Event) => this._handleInputChange(e, item)}"
          @blur="${(e: Event) => this._handleInputBlur(e, item)}"
          @keydown="${(e: KeyboardEvent) => this._handleInputKeydown(e, item)}"
          style="width: ${item.input.width || (isNumber ? '60px' : '120px')}"
          ?disabled="${item.disabled}"
        />
      </div>
    `;
  }

  private _renderColorPicker(item: ToolbarItem) {
    return html`
      <button class="toolbar-btn color-btn ${item.disabled ? 'disabled' : ''}" @click="${(e: Event) => this._handleItemClick(item, e)}" title="${item.tooltip || item.label || ''}" ?disabled="${item.disabled}">
        ${item.icon ? html`<span class="toolbar-icon">${unsafeHTML(item.icon)}</span>` : ''}
        <span class="color-indicator" style="background-color: ${item.value || '#000000'}"></span>
      </button>
    `;
  }

  render() {
    return html`
      <div class="format-toolbar ${this.compact ? 'compact' : ''} ${this.sticky ? 'sticky' : ''} position-${this.position}">
        <div class="toolbar-content">
          ${this.items.map((item) => {
            switch (item.type) {
              case 'button':
                return this._renderButton(item);
              case 'dropdown':
                return this._renderDropdown(item);
              case 'divider':
                return this._renderDivider();
              case 'input':
                return this._renderInput(item);
              case 'color-picker':
                return this._renderColorPicker(item);
              default:
                return '';
            }
          })}
        </div>
      </div>
    `;
  }
}

if (!customElements.get('rd-format-toolbar')) {
  customElements.define('rd-format-toolbar', RdFormatToolbar);
}
